
// apps/admin-ui/src/App.tsx
// Admin Dashboard App with Authentication and Sidebar Layout
// ALL RIGHTS RESERVED
//
// FIXED 2026-02-01: Added routes for drill-down pages (/admin/*)
// UPDATED 2026-02-02: Added DEX OnRamp route
// UPDATED 2026-02-04: Replaced Contracts placeholder with ContractsPage (Rule Browser)
// UPDATED 2026-02-05: Added BlockExplorerPage route
// UPDATED 2026-02-05: Replaced governance DataTablePage stub with full GovernancePage
// UPDATED 2026-02-07: Phase 1 RBAC — all routes gated by RequirePermission

import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Dashboard } from './pages/Dashboard';
import { OrdersPage } from './pages/OrdersPage';
import { DataTablePage } from './pages/DataTablePage';
import { DexOnRampPage } from './pages/DexOnRampPage';
import { ContractsPage } from './pages/ContractsPage';
import { BlockExplorerPage } from './pages/BlockExplorerPage';
import { GovernancePage } from './pages/GovernancePage';
import { LoginPage } from './pages/LoginPage';
import { Layout } from './components/Layout';
import { RequirePermission } from './components/RequirePermission';
import { isAuthenticated } from './utils/auth';

export function App() {
  const [authenticated, setAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  // Check auth on mount
  useEffect(() => {
    setAuthenticated(isAuthenticated());
    setCheckingAuth(false);
  }, []);

  const handleLogin = () => {
    setAuthenticated(true);
  };

  // Show loading while checking auth
  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-surface flex flex-col items-center justify-center gap-4">
        <div className="w-10 h-10 border-3 border-edge border-t-brand-500 rounded-full animate-spin" />
        <p className="text-content-secondary">Loading...</p>
      </div>
    );
  }

  // Show login if not authenticated
  if (!authenticated) {
    return <LoginPage onLogin={handleLogin} />;
  }

  return (
    <BrowserRouter
      future={{
        // Opt-in to v7 behavior for relative route resolution within splat routes
        // This prevents the console warning about changing behavior in v7
        v7_relativeSplatPath: true,
      }}
    >
      <Layout>
        <Routes>
          {/* Main Dashboard — no extra guard; RoleDashboard handles per-role layouts */}
          <Route path="/" element={<Dashboard />} />
          
          {/* ─────────────────────────────────────────────────────────────────────
           * TOP-LEVEL SECTIONS
           * ───────────────────────────────────────────────────────────────────── */}
          <Route path="/orders" element={
            <RequirePermission permission="dashboard:view_transactions">
              <OrdersPage />
            </RequirePermission>
          } />
          <Route path="/contracts" element={
            <RequirePermission permission="contracts:view">
              <ContractsPage />
            </RequirePermission>
          } />
          <Route path="/compliance" element={
            <RequirePermission permission="compliance:kyc_aml_review">
              <CompliancePlaceholder />
            </RequirePermission>
          } />
          <Route path="/treasury" element={
            <RequirePermission permission="reserve:view_balances">
              <TreasuryPlaceholder />
            </RequirePermission>
          } />
          
          {/* ─────────────────────────────────────────────────────────────────────
           * DRILL-DOWN PAGES
           * These routes are navigated to from widget "View Details" links.
           * DataTablePage uses location.pathname to look up config from tableConfigs.ts
           * ───────────────────────────────────────────────────────────────────── */}
          
          {/* Compliance drill-downs */}
          <Route path="/admin/kyc" element={
            <RequirePermission permission="dashboard:view_kyc_queue">
              <DataTablePage />
            </RequirePermission>
          } />
          <Route path="/admin/aml" element={
            <RequirePermission permission="dashboard:view_aml_alerts">
              <DataTablePage />
            </RequirePermission>
          } />
          
          {/* Treasury drill-downs */}
          <Route path="/admin/mints" element={
            <RequirePermission permission="dashboard:view_mint_requests">
              <DataTablePage />
            </RequirePermission>
          } />
          <Route path="/admin/withdrawals" element={
            <RequirePermission permission="dashboard:view_mint_requests">
              <DataTablePage />
            </RequirePermission>
          } />
          <Route path="/admin/transactions" element={
            <RequirePermission permission="dashboard:view_transactions">
              <DataTablePage />
            </RequirePermission>
          } />
          
          {/* DEX drill-downs */}
          <Route path="/admin/dex/onramps" element={
            <RequirePermission permission="dashboard:view_mint_requests">
              <DexOnRampPage />
            </RequirePermission>
          } />
          
          {/* Anchor chain / block explorer */}
          <Route path="/admin/anchor/blocks" element={
            <RequirePermission permission="dashboard:view_anchors">
              <BlockExplorerPage />
            </RequirePermission>
          } />
          
          {/* Security drill-downs */}
          <Route path="/admin/security/vulns" element={
            <RequirePermission permission="dashboard:view_vulnerabilities">
              <DataTablePage />
            </RequirePermission>
          } />
          <Route path="/admin/security/alerts" element={
            <RequirePermission permission="dashboard:view_security_alerts">
              <DataTablePage />
            </RequirePermission>
          } />
          
          {/* Contract drill-downs */}
          <Route path="/admin/contracts" element={
            <RequirePermission permission="contracts:view">
              <DataTablePage />
            </RequirePermission>
          } />
          <Route path="/admin/contracts/reviews" element={
            <RequirePermission permission="contracts:view">
              <DataTablePage />
            </RequirePermission>
          } />
          
          {/* Governance — full voting UI (migrated from CSR) */}
          <Route path="/admin/governance" element={
            <RequirePermission permission="governance:view_proposals">
              <GovernancePage />
            </RequirePermission>
          } />
          
          {/* Audit drill-downs */}
          <Route path="/admin/audit/reports" element={
            <RequirePermission permission="dashboard:view_audit_reports">
              <DataTablePage />
            </RequirePermission>
          } />
          <Route path="/admin/audit/attestation" element={
            <RequirePermission permission="dashboard:view_attestation">
              <DataTablePage />
            </RequirePermission>
          } />
          
          {/* Custody drill-downs */}
          <Route path="/admin/custody/holdings" element={
            <RequirePermission permission="dashboard:view_asset_holdings">
              <DataTablePage />
            </RequirePermission>
          } />
          
          {/* Catch-all: redirect unknown routes to dashboard */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout>
    </BrowserRouter>
  );
}

// Placeholder components (remaining)
function CompliancePlaceholder() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-6">
      <h1 className="text-2xl font-semibold mb-2 text-content">Compliance</h1>
      <p className="text-content-secondary">Compliance reports and SAR/CTR filing coming soon.</p>
    </div>
  );
}

function TreasuryPlaceholder() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-6">
      <h1 className="text-2xl font-semibold mb-2 text-content">Treasury</h1>
      <p className="text-content-secondary">Treasury management coming soon.</p>
    </div>
  );
}

export default App;
